/**
 * Screen-scoped tokens for the **SMS Credits** card on Settings → My Plan,
 * traced from the `SMS credits` block of `Settings.dc.html`.
 *
 * The card is a sibling of the plan, payment-method and invoice cards, so its
 * chrome, type steps and spacing come from `MyPlan.tokens` — only what that
 * block doesn't have lives here. **No new colour is introduced:** every value
 * the mock draws already existed in `theme/colors.ts`, and the two that were a
 * point or two off an existing token (the buy panel's `#FBFAFD` against
 * `signer.surface.subtle`, and the amber low strip against `signer.warn.*`)
 * reuse the established token per the near-duplicate rule rather than adding a
 * second shade for the same role.
 */

import myPlanTokens from "screens/Settings/MyPlan/MyPlan.tokens";
import { signer } from "theme/colors";

const t = myPlanTokens;

export const smsCreditsTokens = {
  grid: {
    /** Balance (wider, it carries the meter) · Used · Last purchase. */
    statStrip: "1.3fr 1fr 1fr",
    /** Activity · Date · Detail · Credits. */
    activityRow: "1.4fr 1fr 1.2fr 90px",
    /**
     * The pack shelf. The count comes from the API, not from here — this is
     * only the desktop track width the design draws for its four.
     */
    packs: "repeat(4, 1fr)",
  },

  /**
   * Rows the activity feed shows before it stops growing the card. The
   * endpoint caps `limit` at 100 and over-reads both halves of the feed to
   * page, so a bigger page costs more than it looks.
   */
  activityLimit: 5,

  /** Status pill beside the title — ACTIVE / RUNNING LOW. */
  badge: {
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: ".08em",
    paddingX: "9px",
    paddingY: "3px",
  },

  /** Balance meter under the headline figure. */
  meter: {
    height: "5px",
    radius: "999px",
    marginTop: "2px",
    track: t.color.meterTrack,
    /** Status colours, so unaffected by the orange→brand rule. */
    healthy: t.color.successText,
    low: t.color.dangerText,
  },

  /** Trailing unit inside the balance figure (" credits left"). */
  balanceUnit: {
    fontSize: "12px",
    fontWeight: 600,
  },

  /** Amber strip under the stats once the wallet is low. */
  lowStrip: {
    padding: "13px 28px",
    gap: "11px",
    iconSize: 16,
    bg: signer.warn.tint,
    border: signer.warn.border,
    text: signer.warn.text,
    icon: signer.warn.main,
    textFontSize: "12.5px",
    textFontWeight: 600,
    linkFontSize: "12px",
    linkFontWeight: 800,
  },

  /**
   * The buy flow is an inline panel that expands inside the card, not an
   * overlay — so it is deliberately not an `AppModal`. The header button
   * toggles it and becomes "Close".
   */
  buyPanel: {
    padding: "22px 28px 24px",
    gap: "16px",
    bg: t.color.headerBg,
    headingFontSize: "13px",
    headingFontWeight: 800,
    packGap: "12px",
  },

  /** One credit-pack card. */
  pack: {
    padding: "16px",
    radius: "12px",
    gap: "4px",
    borderWidth: "1.5px",
    creditsFontSize: "19px",
    labelFontSize: "11px",
    labelLetterSpacing: ".08em",
    priceFontSize: "14px",
    priceMarginTop: "6px",
    unitFontSize: "11px",
    /** Floating "POPULAR" / "BEST VALUE" pill. */
    tagTop: "-9px",
    tagLeft: "14px",
    tagFontSize: "9.5px",
    tagLetterSpacing: ".07em",
    tagPadding: "3px 8px",
  },

  /** "Or enter an amount" row. */
  custom: {
    gap: "12px",
    labelFontSize: "12.5px",
    fieldHeight: "38px",
    fieldRadius: "9px",
    fieldPaddingX: "12px",
    fieldGap: "8px",
    inputWidth: "90px",
    inputFontSize: "13px",
    suffixFontSize: "11.5px",
    hintFontSize: "11.5px",
  },

  /** Order summary bar, and the button that commits the charge. */
  order: {
    padding: "14px 20px",
    radius: "12px",
    gap: "14px",
    lineFontSize: "12.5px",
    noteFontSize: "11.5px",
    totalFontSize: "20px",
    buttonHeight: "40px",
    buttonPaddingX: "20px",
    disabledBg: t.color.disabledControl,
  },
} as const;

export default smsCreditsTokens;
